import { Spinner, SpinnerProps } from 'components/spinner/Spinner';
import { ReactNode } from 'react';
import styled from 'styled-components';

export type SpinnerAreaProps = SpinnerProps &
    Readonly<{
        className?: string;
        children?: ReactNode;
        isLoading?: boolean;
        hideChildren?: boolean;
        /**
         * If true, the spinner area container (which wraps the children components)
         * will be positioned absolutely, as well as the spinner component itself.
         * Generally, you will not need to use this unless you are already using
         * relative/absolute positioning and need more control.
         */
        absolute?: boolean;
        noContainer?: boolean;
        /**
         * When spinner is visible, allow pointer events to pass through to children.
         */
        allowInteraction?: boolean;
        fullHeight?: boolean;
        fullWidth?: boolean;
    }>;

const CenteredSpinner = styled.div`
    z-index: 1000;
    opacity: 0.75;
    & > div {
        margin-left: 5px; // Fix for oddity with spinner
        z-index: 1;
    }
`;

export function SpinnerArea({
    className,
    children,
    isLoading,
    hideChildren,
    absolute,
    noContainer,
    allowInteraction,
    fullHeight = true,
    fullWidth = true,
    ...spinnerProps
}: SpinnerAreaProps) {
    return (
        <div
            className={`${
                absolute ? 'position-absolute inset-0' : 'position-relative'
            } flex-column ${fullHeight ? 'full-height' : ''} ${
                fullWidth ? 'full-width' : ''
            } ${className ?? ''}`}
            style={{
                display: !isLoading && noContainer ? 'contents' : 'flex',
            }}
        >
            {isLoading && (
                <CenteredSpinner
                    data-cy="fade-loader-container"
                    className="position-absolute inset-0 flex-row justify-center align-center"
                    style={{
                        pointerEvents: allowInteraction ? 'none' : 'auto',
                    }}
                >
                    <Spinner {...spinnerProps} />
                </CenteredSpinner>
            )}
            {!(isLoading && hideChildren) && children}
        </div>
    );
}
